"use client";

import { useEffect, useState } from "react";
import { ArrowRight } from "@/components/ui/icons";
import { cn } from "@/lib/utils";

type BackLinkProps = {
  /** Where the control goes when there is nothing on this site to step back to. */
  href: string;
  /** What that fallback destination is called — the tooltip and the accessible name. */
  label: string;
  className?: string;
};

/**
 * The small round back control that opens a page which is always arrived at from somewhere else.
 *
 * "Back" cannot be a fixed destination on these pages: an article is linked from the blog index,
 * from the homepage and from every product page's blog strip, and /products is linked from the
 * homepage suite section and from the Products menu. Someone who arrived from the QuikScale page
 * expects to land there again, not on a listing they never visited.
 *
 * So it stays a real link to `href`, which is both the no-JS behaviour and the right answer for
 * anyone who arrived from a search result or a shared URL with nothing behind them. When the
 * previous page was on this site, the click steps back through history instead.
 */
export function BackLink({ href, label, className }: BackLinkProps) {
  const [fromSite, setFromSite] = useState(false);

  useEffect(() => {
    // The site navigates with full page loads, so the referrer is set for in-site links.
    if (!document.referrer || history.length <= 1) return;
    try {
      const from = new URL(document.referrer);
      setFromSite(from.origin === location.origin && from.pathname !== location.pathname);
    } catch {
      // A malformed referrer is simply not a referrer.
    }
  }, []);

  const name = fromSite ? "Go back" : label;

  return (
    <a
      href={href}
      aria-label={name}
      title={name}
      onClick={(e) => {
        if (!fromSite) return;
        e.preventDefault();
        history.back();
      }}
      className={cn(
        "group grid size-[26px] shrink-0 place-items-center rounded-full bg-panel text-muted transition-colors duration-300 hover:bg-panel-2 hover:text-ink",
        className,
      )}
    >
      <ArrowRight className="size-3.5 rotate-180 transition-transform duration-300 group-hover:-translate-x-0.5" />
    </a>
  );
}
